import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormInputText, Modal, Select } from '@hopskipdrive/react-components';
import * as yup from 'yup';

import { useClosureReasons } from '../../../hooks/useClosureReasons';

const closeAlertValidationSchema = yup.object().shape({
  selectedReason: yup.string().required('You must select a reason for closing the alert.'),
});

export const CloseAlertModal = ({
  userId,
  closeAlert,
  closeAlertType,
  onAlertClosed,
  closeAlertId,
  setCloseAlertId,
}: {
  userId: number;
  closeAlert: (id: number, reason: string, closingUserId: number) => Promise<boolean>;
  closeAlertType: string;
  onAlertClosed: (id: number, success: boolean) => void;
  closeAlertId: number | undefined;
  setCloseAlertId: React.Dispatch<React.SetStateAction<number | undefined>>;
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const { closureReasonOptions = [] } = useClosureReasons();
  const extremeLatenessReasonOptions = ['stakeholders_contacted', 'stakeholders_not_contacted'];
  const isExtremeLateness = closeAlertType === 'extreme_lateness';

  const filteredClosureReasonOptions = useMemo(() => {
    const extremeLatenessKeys = new Set(extremeLatenessReasonOptions);

    return closureReasonOptions.filter((reason) => {
      const isExtremeLatenessReason = extremeLatenessKeys.has(reason.key);

      return isExtremeLateness === isExtremeLatenessReason;
    });
  }, [closureReasonOptions, extremeLatenessReasonOptions, isExtremeLateness]);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{ selectedReason: string }>({
    defaultValues: { selectedReason: '' },
    resolver: yupResolver(closeAlertValidationSchema),
  });

  const handleClose = () => setCloseAlertId(undefined);

  const onSubmit = async (data: { selectedReason: string }) => {
    if (!closeAlertId) return;

    setIsClosing(true);
    try {
      const success = await closeAlert(closeAlertId, data.selectedReason, userId);

      onAlertClosed(closeAlertId, success);
      handleClose();
    } finally {
      setIsClosing(false);
    }
  };

  return (
    <Modal data-testid="close-alert-modal" open={!!closeAlertId} onClose={handleClose}>
      <Modal.Title>Close Alert</Modal.Title>
      <Modal.Content>
        <form id={`close-alert-form-${closeAlertId}`} onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor={`close-reason-select-${closeAlertId}`} style={{ display: 'block', marginBottom: '8px' }}>
            Reason for Closing Alert<span style={{ color: 'red' }}> *</span>
          </label>
          <Controller
            control={control}
            name="selectedReason"
            render={({ field }) => (
              <Select
                id={`close-reason-select-${closeAlertId}`}
                data-testid={`close-reason-select-${closeAlertId}`}
                options={filteredClosureReasonOptions.map((reason) => ({
                  value: reason.key,
                  label: reason.description,
                }))}
                value={field.value}
                setValue={field.onChange}
                renderInput={(inputParams) => (
                  <FormInputText
                    variant="outlined"
                    {...inputParams}
                    placeholder="Select a reason"
                    fullWidth
                    error={!!errors.selectedReason}
                    helperText={errors.selectedReason?.message}
                  />
                )}
              />
            )}
          />
        </form>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="text" onClick={handleClose} disabled={isClosing}>
          Cancel
        </Button>
        <Button
          data-testid={`close-alert-confirm-button-${closeAlertId}`}
          form={`close-alert-form-${closeAlertId}`}
          type="submit"
          loading={isClosing}
          disabled={isClosing}
          style={{
            backgroundColor: 'rgba(255, 218, 212, 1)',
            border: 'none',
            color: 'rgba(221, 55, 48, 1)',
          }}
        >
          {isClosing ? 'Confirming...' : 'Close Alert'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
